import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LayoutDashboard, Briefcase, Clock, FileText, LogOut, Kanban, Users, FileCheck, Users2, Building2, BarChart3, Settings, ChevronDown, ChevronRight, ChevronLeft, Shield, ShieldCheck, CheckCircle, Calendar, ListTree, Calculator, Target, TrendingUp, Award, User, Trophy, MessageSquare, DollarSign, Receipt, ClipboardList, GanttChartSquare } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(true); // Settings section expanded by default
  const [performanceOpen, setPerformanceOpen] = useState(true); // Performance section expanded by default
  const [feedbackOpen, setFeedbackOpen] = useState(true); // Client Feedback section expanded by default
  const [expenseOpen, setExpenseOpen] = useState(true); // Expense Tracking section expanded by default
  const [approvalStats, setApprovalStats] = useState({ pending: 0 });
  const [checkingQueueCount, setCheckingQueueCount] = useState(0);
  // Sidebar collapse state — persisted to localStorage so it survives navigation
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("sidebar_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  // Fetch approval stats for badge
  useEffect(() => {
    const fetchApprovalStats = async () => {
      // Only fetch if user has permission (Manager, Management, Admin)
      // RBAC-based: Fetch approval stats if user has view access to lead approvals
      if (user && user.permissions?.leadApprovals?.view === true) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(`${API}/lead-approvals/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setApprovalStats(response.data);
        } catch (error) {
          console.error("Failed to fetch approval stats:", error);
        }
      }
    };
    
    fetchApprovalStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchApprovalStats, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch checking queue count for badge
  useEffect(() => {
    const fetchCheckingQueue = async () => {
      if (user && user.permissions?.myWorkspace?.view === true) {
        try {
          const token = localStorage.getItem("token");
          const res = await axios.get(`${API}/planning/checking-queue`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCheckingQueueCount(res.data?.count || 0);
        } catch {
          // silent — user may not have any checker assignments
        }
      }
    };
    fetchCheckingQueue();
    const i2 = setInterval(fetchCheckingQueue, 60000);
    return () => clearInterval(i2);
  }, [user]);

  const mainNavItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/", permission: "dashboard" },
    { icon: ClipboardList, label: "My Workspace", path: "/my-workspace", permission: "myWorkspace" },
    { icon: ShieldCheck, label: "Checking Queue", path: "/checking-queue", permission: "myWorkspace", badge: checkingQueueCount || 0 },
    { icon: BarChart3, label: "Estimation Dashboard", path: "/dashboard-estimation", permission: "dashboardEstimation" },
    { icon: BarChart3, label: "Sales", path: "/sales", permission: "sales" },
    { icon: FileText, label: "RFQs", path: "/rfqs", permission: "sales" },
    { icon: Users, label: "Leads", path: "/leads", permission: "leads" },
    { icon: CheckCircle, label: "Lead Approvals", path: "/lead-approvals", permission: "leadApprovals", badge: approvalStats.pending || 0, managerOnly: true },
    { icon: Users, label: "Clients", path: "/clients", permission: "clients" },
    { icon: FileCheck, label: "Estimations", path: "/estimations", permission: "estimations" },
    { icon: Calculator, label: "WBS Estimations", path: "/wbs-estimations", permission: "wbsEstimations" },
    { icon: Briefcase, label: "Projects", path: "/projects", permission: "projects" },
    { icon: LayoutDashboard, label: "Project Planning", path: "/project-planning", permission: "projectPlanning" },
    { icon: Users, label: "Resource Planning", path: "/resource-allocation", permission: "resourceAllocation" },
    { icon: FileText, label: "RFI Log", path: "/rfi-log", permission: "rfiLog" },
    { icon: FileText, label: "Transmittal Log", path: "/transmittal-log", permission: "transmittalLog" },
    { icon: FileText, label: "Change Order Log", path: "/change-order-log", permission: "changeOrderLog" },
    { icon: FileText, label: "Invoices", path: "/invoices", permission: "invoices" },
    { icon: Clock, label: "Timesheets", path: "/timesheets", permission: "timesheets" },
    { icon: Calendar, label: "Timesheet Calendar", path: "/timesheet-calendar", permission: "timesheetCalendar" },
    { icon: BarChart3, label: "Timesheet Reports", path: "/timesheet-reports", permission: "timesheetReports" },
    { icon: Calendar, label: "My Leaves", path: "/leave-management", permission: "leaveManagement" },
    { icon: CheckCircle, label: "Leave Approvals", path: "/leave-approvals", permission: "leaveApprovals" },
    { icon: BarChart3, label: "Leave Reports", path: "/hr/leave-reports", permission: "leaveManagement" },
    { icon: BarChart3, label: "Reports", path: "/reports", permission: "reports" },
    { icon: TrendingUp, label: "Quality Dashboard", path: "/quality-dashboard", permission: "projectPlanning" },
    { icon: Shield, label: "Error Tracking", path: "/error-tracking", permission: "errorTracking" }
  ];

  // Client Feedback Navigation Items
  const clientFeedbackNavItems = [
    { icon: MessageSquare, label: "Client Feedback", path: "/client-feedback", permission: "clientFeedback" },
    { icon: BarChart3, label: "Feedback Reports", path: "/client-feedback/report", permission: "clientFeedbackReport" }
  ];

  const settingsNavItems = [
    { icon: Users2, label: "Users", path: "/users", permission: "users" },
    { icon: Users, label: "Employees", path: "/employees", permission: "employees" },
    { icon: Users, label: "Interns", path: "/interns", permission: "interns", hrOnly: true },
    { icon: Users, label: "Teams", path: "/teams", permission: "teams" },
    { icon: Users, label: "Vendors", path: "/vendors", permission: "vendors" },
    { icon: Calendar, label: "Leave Allocations", path: "/hr/leave-allocations", permission: "hrLeaveAllocations", hrOnly: true },
    { icon: Settings, label: "Leave Policy", path: "/hr/leave-policy", permission: "hrLeaveAllocations", hrOnly: true },
    { icon: Calendar, label: "Holiday Calendar", path: "/hr/holidays", permission: "hrHolidays", hrOnly: true },
    { icon: ListTree, label: "WBS Master List", path: "/settings/wbs-master-list", permission: "wbsMasterList" },
    { icon: Building2, label: "Company Settings", path: "/company-settings", permission: "companySettings" },
    { icon: Shield, label: "Role & Permissions", path: "/permissions", permission: "permissions", adminOnly: true },
    { icon: Shield, label: "Error Settings", path: "/error-settings", permission: "errorSettings", adminOnly: true },
    { icon: Settings, label: "Webhook Settings", path: "/webhook-settings", permission: "webhooks", adminOnly: true }
  ];

  // Performance & Awards Navigation Items
  const performanceNavItems = [
    { icon: BarChart3, label: "KRA Settings", path: "/performance/kra-settings", permission: "kraSettings" },
    { icon: Target, label: "Employee Targets", path: "/performance/employee-targets", permission: "employeeTargets" },
    { icon: TrendingUp, label: "Performance Summary", path: "/performance/summary", permission: "performanceSummary" },
    { icon: Award, label: "Performance Awards", path: "/performance/awards", permission: "performanceAwards" },
    { icon: FileText, label: "Performance Reports", path: "/performance/reports", permission: "performanceReports" },
    { icon: User, label: "My Performance", path: "/performance/my-performance", permission: "myPerformance" }
  ];

  // Expense Tracking Navigation Items
  const expenseNavItems = [
    { icon: Receipt, label: "Expense Entry", path: "/expense/entry", permission: "expenseEntry" },
    { icon: DollarSign, label: "Employee Cost Report", path: "/expense/employee-cost", permission: "expenseEmployeeCost" },
    { icon: Settings, label: "Expense Settings", path: "/expense/settings", permission: "expenseSettings" }
  ];

  // Filter navigation items based on RBAC permissions ONLY - memoized to prevent infinite loops
  const filteredMainNavItems = useMemo(() => {
    if (!user || !user.permissions) return [];
    return mainNavItems.filter(item => {
      const permission = user.permissions[item.permission];
      return permission && permission.view === true;
    });
  }, [user]);

  const filteredSettingsNavItems = useMemo(() => {
    if (!user || !user.permissions) return [];
    return settingsNavItems.filter(item => {
      const permission = user.permissions[item.permission];
      return permission && permission.view === true;
    });
  }, [user]);

  // Filter performance nav items based on RBAC
  const filteredPerformanceNavItems = useMemo(() => {
    if (!user || !user.permissions) return [];
    return performanceNavItems.filter(item => {
      const permission = user.permissions[item.permission];
      return permission && permission.view === true;
    });
  }, [user]);

  // Filter client feedback nav items based on RBAC
  const filteredFeedbackNavItems = useMemo(() => {
    if (!user || !user.permissions) return [];
    return clientFeedbackNavItems.filter(item => {
      const permission = user.permissions[item.permission];
      return permission && permission.view === true;
    });
  }, [user]);

  // Filter expense tracking nav items based on RBAC
  const filteredExpenseNavItems = useMemo(() => {
    if (!user || !user.permissions) return [];
    return expenseNavItems.filter(item => {
      const permission = user.permissions[item.permission];
      return permission && permission.view === true;
    });
  }, [user]);

  // Check if settings section should be visible (at least one settings item accessible)
  const showSettings = filteredSettingsNavItems.length > 0;
  // Check if performance section should be visible
  const showPerformance = filteredPerformanceNavItems.length > 0;
  // Check if client feedback section should be visible
  const showFeedback = filteredFeedbackNavItems.length > 0;
  // Check if expense tracking section should be visible
  const showExpense = filteredExpenseNavItems.length > 0;

  // RBAC-based: Only redirect if on root path
  useEffect(() => {
    if (!user) return;
    
    // Only handle root path redirect
    if (location.pathname === "/") {
      const firstAccessible = filteredMainNavItems[0] || filteredFeedbackNavItems[0] || filteredExpenseNavItems[0] || filteredPerformanceNavItems[0] || filteredSettingsNavItems[0];
      if (firstAccessible) {
        navigate(firstAccessible.path);
      } else {
        navigate("/no-access");
      }
    }
    // If user has no accessible pages and not already on special pages
    else if (
      filteredMainNavItems.length === 0 && 
      filteredFeedbackNavItems.length === 0 &&
      filteredExpenseNavItems.length === 0 &&
      filteredPerformanceNavItems.length === 0 &&
      filteredSettingsNavItems.length === 0 &&
      location.pathname !== "/no-access" && 
      location.pathname !== "/login" &&
      location.pathname !== "/logout"
    ) {
      navigate("/no-access");
    }
  }, [user, location.pathname, filteredMainNavItems, filteredFeedbackNavItems, filteredExpenseNavItems, filteredPerformanceNavItems, filteredSettingsNavItems, navigate]);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-md3-surface" data-testid="layout">
      {/* Sidebar - MD3 Enhanced */}
      <aside className={`${collapsed ? "w-16" : "w-64"} bg-md3-surface-container border-r border-md3-outline-variant fixed h-full flex flex-col shadow-md3-1 transition-[width] duration-200 ease-out z-30`} data-testid="sidebar" data-collapsed={collapsed ? "true" : "false"}>
        {/* Floating collapse/expand toggle - centered vertically */}
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="sidebar-toggle"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-4 top-1/2 -translate-y-1/2 z-40 h-9 w-9 rounded-full bg-md3-primary text-md3-on-primary shadow-md3-2 hover:shadow-md3-3 hover:bg-md3-primary/90 hover:scale-110 active:scale-95 transition-all duration-200 flex items-center justify-center border-2 border-white"
        >
          {collapsed ? <ChevronRight className="w-5 h-5" strokeWidth={3} /> : <ChevronLeft className="w-5 h-5" strokeWidth={3} />}
        </button>

        {/* Header - Fixed */}
        <div className={`${collapsed ? "p-3" : "p-6"} flex-shrink-0 border-b border-md3-outline-variant`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            <img 
              src="/logo.png" 
              alt="Milekal Logo" 
              className={`${collapsed ? "h-9" : "h-12"} w-auto object-contain`}
            />
            {!collapsed && (
              <h1 className="text-md3-title-large text-md3-on-surface font-normal">Milekal Pulse</h1>
            )}
          </div>
        </div>

        {/* Navigation - Scrollable - MD3 Enhanced */}
        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1 mt-2">
            {/* Main Navigation Items */}
            {filteredMainNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                title={collapsed ? item.label : undefined}
                className={`w-full flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-4"} py-3 rounded-md3-full transition-all duration-200 relative ${
                  isActive(item.path)
                    ? "bg-md3-secondary-container text-md3-on-secondary-container shadow-md3-1"
                    : "text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="text-md3-label-large flex-1 text-left">{item.label}</span>
                    {item.badge > 0 && (
                      <Badge className="bg-md3-error text-md3-on-error text-xs px-2.5 py-0.5 rounded-md3-full shadow-md3-1">
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
                {collapsed && item.badge > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-md3-error text-md3-on-error text-[10px] font-semibold flex items-center justify-center shadow-md3-1">
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </button>
            ))}

            {/* Client Feedback Section - MD3 Enhanced */}
            {showFeedback && (
              <div className="pt-4 border-t border-md3-outline-variant mt-2">
                {/* Feedback Header - Collapsible (hidden when sidebar collapsed) */}
                {!collapsed && (
                  <button
                    onClick={() => setFeedbackOpen(!feedbackOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md3-full transition-all duration-200 text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                  >
                    <MessageSquare className="w-5 h-5" />
                    <span className="text-md3-label-large flex-1 text-left">Client Feedback</span>
                    {feedbackOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Feedback Sub-items */}
                {(collapsed || feedbackOpen) && (
                  <div className={collapsed ? "space-y-1" : "ml-2 mt-1 space-y-1"}>
                    {filteredFeedbackNavItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        title={collapsed ? item.label : undefined}
                        className={`w-full flex items-center ${collapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-2.5"} rounded-md3-full transition-all duration-200 ${
                          isActive(item.path)
                            ? "bg-md3-secondary-container text-md3-on-secondary-container shadow-md3-1"
                            : "text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                        }`}
                      >
                        <item.icon className={collapsed ? "w-5 h-5" : "w-4 h-4"} />
                        {!collapsed && <span className="text-md3-label-medium">{item.label}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Expense Tracking Section - MD3 Enhanced */}
            {showExpense && (
              <div className="pt-4 border-t border-md3-outline-variant mt-2">
                {/* Expense Header - Collapsible (hidden when sidebar collapsed) */}
                {!collapsed && (
                  <button
                    onClick={() => setExpenseOpen(!expenseOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md3-full transition-all duration-200 text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                  >
                    <DollarSign className="w-5 h-5" />
                    <span className="text-md3-label-large flex-1 text-left">Expense Tracking</span>
                    {expenseOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Expense Sub-items */}
                {(collapsed || expenseOpen) && (
                  <div className={collapsed ? "space-y-1" : "ml-2 mt-1 space-y-1"}>
                    {filteredExpenseNavItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        title={collapsed ? item.label : undefined}
                        className={`w-full flex items-center ${collapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-2.5"} rounded-md3-full transition-all duration-200 ${
                          isActive(item.path)
                            ? "bg-md3-secondary-container text-md3-on-secondary-container shadow-md3-1"
                            : "text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                        }`}
                      >
                        <item.icon className={collapsed ? "w-5 h-5" : "w-4 h-4"} />
                        {!collapsed && <span className="text-md3-label-medium">{item.label}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Performance & Awards Section - MD3 Enhanced */}
            {showPerformance && (
              <div className="pt-4 border-t border-md3-outline-variant mt-2">
                {/* Performance Header - Collapsible (hidden when sidebar collapsed) */}
                {!collapsed && (
                  <button
                    onClick={() => setPerformanceOpen(!performanceOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md3-full transition-all duration-200 text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                  >
                    <Trophy className="w-5 h-5" />
                    <span className="text-md3-label-large flex-1 text-left">Performance & Awards</span>
                    {performanceOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Performance Sub-items */}
                {(collapsed || performanceOpen) && (
                  <div className={collapsed ? "space-y-1" : "ml-2 mt-1 space-y-1"}>
                    {filteredPerformanceNavItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                        title={collapsed ? item.label : undefined}
                        className={`w-full flex items-center ${collapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-2.5"} rounded-md3-full transition-all duration-200 ${
                          isActive(item.path)
                            ? "bg-md3-secondary-container text-md3-on-secondary-container shadow-md3-1"
                            : "text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                        }`}
                      >
                        <item.icon className={collapsed ? "w-5 h-5" : "w-4 h-4"} />
                        {!collapsed && <span className="text-md3-label-medium">{item.label}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings Section - MD3 Enhanced */}
            {showSettings && (
              <div className="pt-4 border-t border-md3-outline-variant mt-2">
                {/* Settings Header - Collapsible (hidden when sidebar collapsed) */}
                {!collapsed && (
                  <button
                    onClick={() => setSettingsOpen(!settingsOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md3-full transition-all duration-200 text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                  >
                    <Settings className="w-5 h-5" />
                    <span className="text-md3-label-large flex-1 text-left">Settings</span>
                    {settingsOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Settings Sub-items */}
                {(collapsed || settingsOpen) && (
                  <div className={collapsed ? "space-y-1" : "ml-2 mt-1 space-y-1"}>
                    {filteredSettingsNavItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                        title={collapsed ? item.label : undefined}
                        className={`w-full flex items-center ${collapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-2.5"} rounded-md3-full transition-all duration-200 ${
                          isActive(item.path)
                            ? "bg-md3-secondary-container text-md3-on-secondary-container shadow-md3-1"
                            : "text-md3-on-surface-variant hover:bg-md3-surface-container-high"
                        }`}
                      >
                        <item.icon className={collapsed ? "w-5 h-5" : "w-4 h-4"} />
                        {!collapsed && <span className="text-md3-label-medium">{item.label}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* No Access Message - MD3 Enhanced */}
          {filteredMainNavItems.length === 0 && filteredExpenseNavItems.length === 0 && filteredPerformanceNavItems.length === 0 && filteredSettingsNavItems.length === 0 && (
            <div className="px-4 py-4 bg-amber-100 border-0 rounded-md3-md mx-3 mb-4 shadow-md3-1">
              <p className="text-md3-label-large text-amber-900 font-medium">No Accessible Pages</p>
              <p className="text-md3-body-small text-amber-800 mt-1">
                Contact your administrator to grant you access to pages.
              </p>
            </div>
          )}

        {/* User Profile - Fixed at Bottom - MD3 Enhanced */}
        <div className={`flex-shrink-0 ${collapsed ? "p-2" : "p-4"} border-t border-md3-outline-variant bg-md3-surface-container`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-10 h-10 rounded-md3-full bg-gradient-to-br from-md3-primary to-md3-tertiary flex items-center justify-center text-md3-on-primary font-semibold shadow-md3-1 text-base"
                title={`${user?.first_name || ""} ${user?.last_name || ""}`.trim()}
              >
                {user?.first_name?.charAt(0)?.toUpperCase()}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 bg-md3-error-container hover:bg-md3-error text-md3-on-error-container hover:text-md3-on-error border-0 shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                onClick={handleLogout}
                data-testid="logout-button"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4 p-2 rounded-md3-md bg-md3-surface-container-high">
                <div className="w-12 h-12 rounded-md3-full bg-gradient-to-br from-md3-primary to-md3-tertiary flex items-center justify-center text-md3-on-primary font-semibold shadow-md3-1 text-lg">
                  {user?.first_name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-md3-title-small text-md3-on-surface truncate font-medium">{user?.first_name} {user?.last_name || ''}</p>
                  <p className="text-md3-body-small text-md3-on-surface-variant truncate">{user?.access_level || user?.role}</p>
                  {user?.department && <p className="text-md3-label-small text-md3-on-surface-variant truncate">{user?.department}</p>}
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full bg-md3-error-container hover:bg-md3-error text-md3-on-error-container hover:text-md3-on-error border-0 shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </>
          )}
        </div>
      </aside>

      {/* Main Content - MD3 Enhanced */}
      <main className={`flex-1 ${collapsed ? "ml-16" : "ml-64"} bg-md3-surface transition-[margin] duration-200 ease-out`}>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}